import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '../SiteSettingsContext';
import { useAuth } from '../auth/AuthContext';
import {
  initGA,
  isAnalyticsSuppressed,
  resolveGa4MeasurementId,
  setAnalyticsSuppressed,
  trackPageView,
} from '../analytics';
import { clientErrorMessageFromEvent, isIgnorableClientError } from '../errorReporting';

/** Loads GA4 when a measurement ID is configured; tracks SPA page views. */
export default function Ga4Init() {
  const { settings: site } = useSiteSettings();
  const { ready, isStaff } = useAuth();
  const location = useLocation();

  // Suppress GA4 while auth is still resolving and for signed-in admin/staff, so
  // internal browsing never reaches Google Analytics and pollutes customer data.
  useEffect(() => {
    setAnalyticsSuppressed(!ready || isStaff);
  }, [ready, isStaff]);

  useEffect(() => {
    if (!ready || isStaff) return;
    const id = resolveGa4MeasurementId(site);
    if (!id) return;
    if (initGA(id)) {
      const path = `${location.pathname}${location.search}${location.hash}`;
      trackPageView(path);
    }
  }, [ready, isStaff, site, location.pathname, location.search, location.hash]);

  // Global JS error → GA4 exception event (fires only after GA4 is initialised).
  // Dedupe identical messages and cap the total so a render/error loop can't flood GA4.
  const errorReportRef = useRef({ seen: new Set(), count: 0 });
  useEffect(() => {
    const handleError = (event) => {
      if (isAnalyticsSuppressed() || typeof window.gtag !== 'function') return;
      const description = event?.reason != null
        ? String(event.reason instanceof Error ? event.reason.message : event.reason || 'Unknown error')
        : clientErrorMessageFromEvent(event);
      if (isIgnorableClientError(description, {
        name: event?.error?.name || (event?.reason instanceof Error ? event.reason.name : undefined),
        sourceFile: event?.filename,
      })) return;
      const state = errorReportRef.current;
      if (state.count >= 10) return;
      if (state.seen.has(description)) return;
      state.seen.add(description);
      state.count += 1;
      window.gtag('event', 'exception', { description: description.slice(0, 150), fatal: false });
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  return null;
}
